import { Download, Eye, ImagePlus, RotateCcw, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  MAX_POST_POINTS,
  MAX_POST_REFERENCES,
  POST_INSTRUCTIONS_MAX,
  POST_PERSON_AGES,
  POST_PERSON_AGE_LABELS,
  POST_PERSON_DRESS,
  POST_PERSON_DRESS_LABELS,
  POST_PERSON_GENDERS,
  POST_PERSON_GENDER_LABELS,
  POST_PICTURE_KINDS,
  POST_PICTURE_KIND_LABELS,
  POST_REFERENCE_ROLES,
  POST_REFERENCE_ROLE_LABELS,
  POST_WORD_LIMITS,
  jobPostWordsSchema,
  samePostDesign,
  samePostWords,
  type JobDetail,
  type JobPostDesign,
  type JobPostWords,
  type PostPictureKind,
  type PostReferenceRole,
} from '@nest-recruit/shared';
import { Button, buttonClassName } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { Input, Textarea } from '../../../../components/Input';
import { Segmented } from '../../../../components/Segmented';
import { Select } from '../../../../components/Select';
import { useCanEdit } from '../../../../stores/session';
import { jobImageVersionUrl, useJobImageRun, useJobImageVersions, useJobPostWords, useMakeJobImage, useRestoreJobImage, useSaveJobPostWords } from '../../api/job-image-api';
import { jobFileUrl } from '../../api/jobs-api';
import { postReferenceUrl, useJobImageSpend, useJobPostDesign, useJobPostDesignActions, useJobPostPrompt } from '../../api/post-design-api';
import { FileButton } from '../builder/BriefEditor';
import { useAttachJobFile, useRemoveJobFile } from '../builder/job-files';
import { ColourToolbar } from './ColourToolbar';
import { PostPreview } from './PostPreview';
import styles from './PostStep.module.css';

type SingleLine = 'headline' | 'role' | 'facts' | 'button' | 'applyBy' | 'applyAt';
type Line = { readonly key: SingleLine; readonly label: string; readonly max: number };

const OPENING: readonly Line[] = [
  { key: 'headline', label: 'Headline', max: POST_WORD_LIMITS.headline },
  { key: 'role', label: 'Role', max: POST_WORD_LIMITS.role },
  { key: 'facts', label: 'Facts', max: POST_WORD_LIMITS.facts },
];
const CLOSING: readonly Line[] = [
  { key: 'button', label: 'Button', max: POST_WORD_LIMITS.button },
  { key: 'applyBy', label: 'Apply by', max: POST_WORD_LIMITS.applyBy },
  { key: 'applyAt', label: 'Apply at', max: POST_WORD_LIMITS.applyAt },
];
const POINT_SLOTS = Array.from({ length: MAX_POST_POINTS }, (_, index) => index);
const IMAGE_ACCEPT = '.png,.jpg,.jpeg';
const MICROS_PER_DOLLAR = 1_000_000;
const CENTS_PER_IMAGE = 10;

type View = 'preview' | 'image';

/** The palette and fonts match the venture's brand kit. */
function sameLook(design: JobPostDesign, look: Pick<JobPostDesign, 'palette' | 'headlineFont' | 'bodyFont'>): boolean {
  return JSON.stringify(design.palette) === JSON.stringify(look.palette) && design.headlineFont === look.headlineFont && design.bodyFont === look.bodyFont;
}

function tidy(words: JobPostWords): JobPostWords {
  return { ...words, points: words.points.map((point) => point.trim()).filter(Boolean) };
}

function options<T extends string>(values: readonly T[], labels: Readonly<Record<T, string>>): { value: T; label: string }[] {
  return values.map((value) => ({ value, label: labels[value] }));
}

function isPictureKind(value: string): value is PostPictureKind {
  return (POST_PICTURE_KINDS as readonly string[]).includes(value);
}

function isReferenceRole(value: string): value is PostReferenceRole {
  return (POST_REFERENCE_ROLES as readonly string[]).includes(value);
}

/** The server's words unless the person has typed something not yet saved. */
function useDraft<T>(server: T | undefined, same: (a: T, b: T) => boolean): readonly [T | null, (next: T) => void] {
  const [draft, setDraft] = useState<T | null>(null);
  const synced = useRef<T | null>(null);
  useEffect(() => {
    if (server === undefined) return;
    const last = synced.current;
    setDraft((current) => (current === null || (last !== null && same(current, last)) ? server : current));
    synced.current = server;
  }, [server, same]);
  return [draft, setDraft];
}

/**
 * ADR 0066 — the Job post section (JOB-13, `job_image`). The words come from the job and the design from the venture's
 * brand kit; changing either is free and shows at once in the live preview. An image is made, and paid for, only on
 * Approve (owner, 2026-10-28). Uploading a finished image of your own stays the other way.
 */
export function PostStep({ job, isReadOnly }: { readonly job: JobDetail; readonly isReadOnly: boolean }): ReactNode {
  const wordsView = useJobPostWords(job.id).data;
  const designView = useJobPostDesign(job.id).data;
  const run = useJobImageRun(job.id).data ?? null;
  const versions = useJobImageVersions(job.id).data ?? [];
  const spend = useJobImageSpend(job.id).data;
  const { save: saveWords, reset: resetWords } = useSaveJobPostWords(job.id);
  const designActions = useJobPostDesignActions(job.id);
  const { make } = useMakeJobImage(job.id);
  const { restore } = useRestoreJobImage(job.id);
  const prompt = useJobPostPrompt(job.id);
  const { attach, isAttaching } = useAttachJobFile(job);
  const files = useRemoveJobFile(job);
  const canSaveToBrandKit = useCanEdit('hiring_setup');
  const [draft, setDraft] = useDraft(wordsView?.words, samePostWords);
  const [instructions, setInstructions] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);

  if (!wordsView || !designView || !draft) return null;
  const design = designView.design;
  const words = tidy(draft);
  const parsed = jobPostWordsSchema.safeParse(words);
  const wordsProblem = parsed.success ? null : (parsed.error.issues[0]?.message ?? 'Check the words.');
  const image = job.imageDocumentId && !files.hidden.has(job.imageDocumentId) ? (job.files.find((file) => file.id === job.imageDocumentId) ?? null) : null;
  const current = versions.find((version) => version.isCurrent) ?? null;
  const isMaking = run?.status === 'making';
  const canEdit = !isReadOnly && !isMaking;
  const wordsChanged = current?.words ? !samePostWords(current.words, words) : true;
  const designChanged = current?.design ? !samePostDesign(current.design, design) : true;
  const canUpdate = Boolean(current?.words) && wordsChanged && !designChanged;
  const shown: View = view ?? (image ? 'image' : 'preview');
  const photo = design.references.find((reference) => reference.role === 'include');

  const saveDesign = (change: Partial<JobPostDesign>): void => designActions.save({ ...design, ...change });
  const commitWords = (): void => {
    if (wordsProblem || samePostWords(words, wordsView.words)) return;
    saveWords(words);
  };
  const setLine = (key: SingleLine, value: string): void => setDraft({ ...draft, [key]: value });
  const setPoint = (index: number, value: string): void => setDraft({ ...draft, points: POINT_SLOTS.map((slot) => (slot === index ? value : (draft.points[slot] ?? ''))) });
  const approve = (mode: 'new' | 'edit'): void => {
    if (wordsProblem) return;
    make({ words, design, mode });
    setView('image');
  };
  const lineField = (line: Line): ReactNode => (
    <label key={line.key} className={styles.line}>
      <span className={styles.label}>{line.label}</span>
      <Input value={draft[line.key]} maxLength={line.max} disabled={!canEdit} onChange={(event) => setLine(line.key, event.target.value)} onBlur={commitWords} />
    </label>
  );
  const primaryLabel = !current?.words ? 'Approve & make image' : canUpdate ? 'Update image' : 'Make a fresh image';

  return (
    <div className={styles.post}>
      <div className={styles.side}>
        <div className={styles.stage}>
          {image && <Segmented<View> label="Show" value={shown} options={[{ value: 'preview', label: 'Live preview' }, { value: 'image', label: 'Made image' }]} onChange={setView} />}
          {shown === 'image' && image ? (
            <img className={styles.made} src={jobFileUrl(job.id, image.id)} alt={`Job post image: ${image.fileName}`} />
          ) : (
            <PostPreview
              words={words}
              design={design}
              company={job.ventureName}
              picture={design.picture.kind === 'auto' ? 'person' : design.picture.kind}
              photoUrl={photo ? postReferenceUrl(job.id, photo.documentId) : null}
            />
          )}
          {shown === 'preview' && <p className={styles.note}>A quick preview drawn by the app. The AI draws the real picture and lettering.</p>}
        </div>

        <ColourToolbar
          palette={design.palette}
          headlineFont={design.headlineFont}
          bodyFont={design.bodyFont}
          disabled={!canEdit}
          canSaveToBrandKit={canSaveToBrandKit && !isReadOnly}
          onPalette={(palette) => saveDesign({ palette })}
          onFonts={(fonts) => saveDesign(fonts)}
          onSaveToBrandKit={designActions.saveAsVentureDefault}
        />

        {!isReadOnly && (
          <div className={styles.actions}>
            <Button variant="solid" icon={ImagePlus} isPending={isMaking} disabled={wordsProblem !== null} onClick={() => approve(canUpdate ? 'edit' : 'new')}>
              {primaryLabel}
            </Button>
            {canUpdate && (
              <Button variant="ghost" disabled={isMaking || wordsProblem !== null} onClick={() => approve('new')}>
                Make a fresh image
              </Button>
            )}
            <Button variant="ghost" icon={Eye} disabled={wordsProblem !== null} onClick={() => prompt.preview({ words, design, mode: canUpdate ? 'edit' : 'new' })}>
              Show instructions
            </Button>
          </div>
        )}
        <p className={styles.status} role="status">
          {isMaking
            ? 'Making the image. It takes about 20 seconds.'
            : `1 image · about ${CENTS_PER_IMAGE}¢ · about 20 seconds. Changing words, colours and fonts is free.`}
          {spend && ` This month: ${spend.images} image${spend.images === 1 ? '' : 's'}, $${(spend.microUsd / MICROS_PER_DOLLAR).toFixed(2)}.`}
        </p>
        {run?.status === 'failed' && run.problem && (
          <p className={styles.problem} role="alert">
            {run.problem}
          </p>
        )}
        {wordsProblem && (
          <p className={styles.problem} role="alert">
            {wordsProblem}
          </p>
        )}

        {prompt.result && (
          <div className={styles.prompt}>
            <div className={styles.promptHead}>
              <span className={styles.label}>
                What will be sent to Nano Banana{prompt.result.pictureCount > 0 ? `, with ${prompt.result.pictureCount} picture${prompt.result.pictureCount === 1 ? '' : 's'}` : ''}
              </span>
              <Button variant="ghost" size="sm" icon={X} onClick={prompt.clear}>
                Hide
              </Button>
            </div>
            <pre className={styles.promptText}>{prompt.result.prompt}</pre>
            {design.picture.gender === 'any' && <p className={styles.note}>"Any" person details are chosen afresh for each image, so this changes a little every time.</p>}
          </div>
        )}

        <div className={styles.fileRow}>
          {!isReadOnly && (
            <FileButton label="Upload your own finished image" accept={IMAGE_ACCEPT} isPending={isAttaching} onFile={(file) => void attach(file, { isImage: true })}>
              Upload your own
            </FileButton>
          )}
          {image && !isMaking && (
            <a className={buttonClassName('ghost', 'sm')} href={jobFileUrl(job.id, image.id)} download={image.fileName}>
              <Download size={14} aria-hidden />
              Download
            </a>
          )}
          {image && !isReadOnly && (
            <Button variant="ghost" size="sm" icon={Trash2} onClick={() => files.remove(image.id, image.fileName)}>
              Remove image
            </Button>
          )}
        </div>

        {versions.length > 1 && (
          <div className={styles.versions}>
            <span className={styles.label}>Earlier images</span>
            <div className={styles.versionStrip}>
              {versions.map((version) => (
                <button
                  key={version.runId}
                  type="button"
                  className={styles.version}
                  data-current={version.isCurrent}
                  disabled={isReadOnly || version.isCurrent || isMaking}
                  aria-label={version.isCurrent ? 'The image in use' : 'Go back to this image'}
                  title={version.isCurrent ? 'In use' : 'Go back to this image'}
                  onClick={() => restore(version.runId)}
                >
                  <img src={jobImageVersionUrl(job.id, version.runId)} alt="" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.controls}>
        <Card
          title="Post words"
          meta="Filled in from the job"
          action={
            !isReadOnly && wordsView.isEdited ? (
              <Button variant="ghost" size="sm" icon={RotateCcw} disabled={isMaking} onClick={resetWords}>
                Reset to job details
              </Button>
            ) : undefined
          }
        >
          <div className={styles.fields}>
            {OPENING.map(lineField)}
            {POINT_SLOTS.map((index) => (
              <label key={`point-${index}`} className={styles.line}>
                <span className={styles.label}>Point {index + 1}</span>
                <Input value={draft.points[index] ?? ''} maxLength={POST_WORD_LIMITS.point} disabled={!canEdit} placeholder="Leave empty to skip" onChange={(event) => setPoint(index, event.target.value)} onBlur={commitWords} />
              </label>
            ))}
            {CLOSING.map(lineField)}
          </div>
        </Card>

        <Card title="Picture" meta="What stands on the big shape">
          <div className={styles.fields}>
            <Segmented<PostPictureKind>
              label="Picture"
              value={design.picture.kind}
              options={options(POST_PICTURE_KINDS, POST_PICTURE_KIND_LABELS)}
              disabled={!canEdit}
              onChange={(kind) => isPictureKind(kind) && saveDesign({ picture: { ...design.picture, kind } })}
            />
            {design.picture.kind === 'auto' && <p className={styles.note}>A person for office and desk jobs, symbolic objects for hands-on jobs.</p>}
            {design.picture.kind === 'own_photo' && !photo && <p className={styles.problem}>Add your photo under Your pictures, marked "Put this in the post".</p>}
            {(design.picture.kind === 'person' || design.picture.kind === 'auto') && (
              <div className={styles.personRow}>
                <Select label="Who" value={design.picture.gender} options={options(POST_PERSON_GENDERS, POST_PERSON_GENDER_LABELS)} disabled={!canEdit} onChange={(value) => { const gender = POST_PERSON_GENDERS.find((option) => option === value); if (gender) saveDesign({ picture: { ...design.picture, gender } }); }} />
                <Select label="Age" value={design.picture.age} options={options(POST_PERSON_AGES, POST_PERSON_AGE_LABELS)} disabled={!canEdit} onChange={(value) => { const age = POST_PERSON_AGES.find((option) => option === value); if (age) saveDesign({ picture: { ...design.picture, age } }); }} />
                <Select label="Clothes" value={design.picture.dress} options={options(POST_PERSON_DRESS, POST_PERSON_DRESS_LABELS)} disabled={!canEdit} onChange={(value) => { const dress = POST_PERSON_DRESS.find((option) => option === value); if (dress) saveDesign({ picture: { ...design.picture, dress } }); }} />
              </div>
            )}
          </div>
        </Card>

        <Card title="Your pictures" meta={`Up to ${MAX_POST_REFERENCES}: a real photo to use, or a post whose style you like`}>
          <div className={styles.fields}>
            {design.references.map((reference) => (
              <div key={reference.documentId} className={styles.reference}>
                <img className={styles.referenceThumb} src={postReferenceUrl(job.id, reference.documentId)} alt="" />
                <span className={styles.referenceName}>{reference.fileName}</span>
                <Select
                  label={`What ${reference.fileName} is for`}
                  value={reference.role}
                  options={options(POST_REFERENCE_ROLES, POST_REFERENCE_ROLE_LABELS)}
                  disabled={!canEdit}
                  onChange={(value) => isReferenceRole(value) && saveDesign({ references: design.references.map((item) => (item.documentId === reference.documentId ? { ...item, role: value } : item)) })}
                />
                {!isReadOnly && (
                  <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove ${reference.fileName}`} disabled={isMaking} onClick={() => designActions.removePicture(reference.documentId)} />
                )}
              </div>
            ))}
            {!isReadOnly && design.references.length < MAX_POST_REFERENCES && (
              <FileButton label="Add a picture" accept={IMAGE_ACCEPT} isPending={designActions.isAddingPicture} onFile={designActions.addPicture}>
                <Upload size={14} aria-hidden /> Add a picture
              </FileButton>
            )}
          </div>
        </Card>

        <Card title="Your instructions" meta="Optional, added to the house rules">
          <div className={styles.fields}>
            <Textarea
              rows={3}
              maxLength={POST_INSTRUCTIONS_MAX}
              disabled={!canEdit}
              placeholder='For example: "Make it feel festive for Eid" or "Show a woman in a lab coat"'
              value={instructions ?? design.instructions}
              onChange={(event) => setInstructions(event.target.value)}
              onBlur={() => {
                if (instructions !== null && instructions.trim() !== design.instructions) saveDesign({ instructions: instructions.trim() });
                setInstructions(null);
              }}
            />
            <p className={styles.note}>
              {(instructions ?? design.instructions).length} of {POST_INSTRUCTIONS_MAX}. The house rules still win: no gradients, no extra words, no salary or contact details.
            </p>
            {!isReadOnly && !sameLook(design, designView.ventureDefault) && (
              <Button variant="ghost" size="sm" icon={RotateCcw} disabled={!canEdit} onClick={designActions.reset}>
                Colours and fonts back to the brand kit
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
